import React, {createContext, useContext, useMemo} from 'react';
import {Overwrite} from 'utility-types';
import {TokenizerConfig} from '../api/generated/index.defs';
import {AnyObject} from '../types';

export type PlaidLinkStatus = 'Disabled' | 'Optional' | 'Required';

interface Context {
  cardConfig?: TokenizerConfig;
  plaidLinkStatus?: PlaidLinkStatus;
  hostingMode?: 'embedded' | 'default';
  embeddedContext?: {
    parentDomain?: string;
    parentWindowWidth?: number;
  };
}

type TypedConfig<T = AnyObject> = Overwrite<
  TokenizerConfig,
  {
    properties: T;
  }
>;
interface TokenizationContextProvider extends Context {
  children: React.ReactNode;
}

export const TokenizationContext = createContext<Context>({});

const TokenizationContextProvider: React.FC<TokenizationContextProvider> = (props) => {
  const value = useMemo(
    () => ({
      cardConfig: props.cardConfig,
      plaidLinkStatus: props.plaidLinkStatus,
      hostingMode: props.hostingMode,
      embeddedContext: props.embeddedContext,
    }),
    [props.cardConfig, props.embeddedContext, props.hostingMode, props.plaidLinkStatus]
  );
  return (
    <TokenizationContext.Provider value={value}>{props.children}</TokenizationContext.Provider>
  );
};

function useTokenizationConfig<T = AnyObject>(): TypedConfig<T> | undefined {
  const state = useContext(TokenizationContext);
  if (!state) {
    throw new Error(`useTokenizationConfig must be used within a TokenizationContextProvider`);
  }
  return state.cardConfig as unknown as TypedConfig<T>;
}

function usePlaidLinkConfig() {
  const state = useContext(TokenizationContext);
  return state.plaidLinkStatus || 'Disabled';
}

function useTokenizationContext() {
  const state = useContext(TokenizationContext);
  return state;
}

export {
  TokenizationContextProvider,
  useTokenizationConfig,
  usePlaidLinkConfig,
  useTokenizationContext,
};
